import { useCallback, useEffect, useMemo, useState } from 'react';
import { SupportTicketFilters, supportService, SupportTicket } from '../services/support.service';

interface UseSupportTicketsReturn {
  tickets: SupportTicket[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: SupportTicketFilters;
  setFilters: (filters: SupportTicketFilters) => void;
  refetch: () => void;
  summary: {
    open: number;
    inProgress: number;
    resolved: number;
  };
}

export const useSupportTickets = (initialFilters: SupportTicketFilters = {}): UseSupportTicketsReturn => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<SupportTicketFilters>({ page: 1, limit: 20, ...initialFilters });
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await supportService.getTickets(filters);
      setTickets(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch support tickets'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const summary = useMemo(() => {
    return {
      open: tickets.filter((t) => t.status === 'open').length,
      inProgress: tickets.filter((t) => t.status === 'in_progress').length,
      resolved: tickets.filter((t) => t.status === 'resolved').length,
    };
  }, [tickets]);

  return {
    tickets,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchTickets,
    summary,
  };
};

export default useSupportTickets;
